import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Card, Spinner } from "react-bootstrap";
import * as Icon from "react-bootstrap-icons";
import GenerationService from "../services/GenerationService";
import { Individual, Gender } from "../models/Individual";
import { MessageType } from "../models/Message";
import GenerationTree from "../components/GenerationTree/GenerationTree";
import { useAlertMessage } from "../components/common/AlertHandler";
import { GenTag } from "../components/common/IndividualTag";
import LoadingSpinner from "../components/common/LoadingSpinner";

const MAX_DEPTH = 10;

const UserFullGenerationTreePage = () => {
    const params = useParams();
    const userId = params["id"] as string;

    const { setErrorMsg } = useAlertMessage();

    const [user, setUser] = useState<Individual | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    useEffect(() => {
        if (!userId) return;

        let isMounted = true;

        const loadFullGeneration = async () => {
            setIsLoading(true);
            setUser(null);

            try {
                // 1. Fetch root individual details
                const rootUser = await GenerationService.getIndividual(userId);
                if (!isMounted) return;

                const rootCopy: Individual = {
                    ...rootUser,
                    children: []
                };

                const individualsById = new Map<string, Individual>([[rootCopy.id, rootCopy]]);
                const childrenByFatherId = new Map<string, Map<string, Individual>>();
                const loadedDescendantIds = new Set<string>();
                let currentLevel: Individual[] = [rootCopy];
                let count = 0;

                // 2. Load uncovered descendants a level at a time, reusing each response's depth window.
                while (currentLevel.length > 0 && isMounted) {
                    const usersToLoad = currentLevel.filter(ind => !loadedDescendantIds.has(ind.id));
                    const results = await Promise.all(
                        usersToLoad.map(ind => GenerationService.getDescendants(ind.id, MAX_DEPTH))
                    );

                    if (!isMounted) return;

                    for (let i = 0; i < usersToLoad.length; i++) {
                        const individual = usersToLoad[i];
                        const descendants = results[i] || [];

                        for (const descendant of descendants) {
                            let storedDescendant = individualsById.get(descendant.id);
                            if (!storedDescendant) {
                                storedDescendant = { ...descendant, children: [] };
                                individualsById.set(descendant.id, storedDescendant);
                            } else {
                                Object.assign(storedDescendant, descendant, { children: storedDescendant.children || [] });
                            }

                            if (descendant.fatherId) {
                                let children = childrenByFatherId.get(descendant.fatherId);
                                if (!children) {
                                    children = new Map<string, Individual>();
                                    childrenByFatherId.set(descendant.fatherId, children);
                                }
                                children.set(storedDescendant.id, storedDescendant);
                            }
                        }

                        // A response covers the queried user and each descendant less than MAX_DEPTH levels away.
                        let coveredLevel = [individual];
                        for (let depth = 0; depth < MAX_DEPTH && coveredLevel.length > 0; depth++) {
                            coveredLevel.forEach(covered => loadedDescendantIds.add(covered.id));
                            coveredLevel = coveredLevel.flatMap(covered =>
                                Array.from(childrenByFatherId.get(covered.id)?.values() || [])
                            );
                        }
                    }

                    const nextLevel: Individual[] = [];
                    for (const individual of currentLevel) {
                        individual.children = Array.from(childrenByFatherId.get(individual.id)?.values() || []);
                        nextLevel.push(...individual.children.filter(child => child.gender !== Gender.F));
                    }

                    // Update tree data state incrementally as levels load.
                    setUser(JSON.parse(JSON.stringify(rootCopy)));
                    currentLevel = nextLevel;
                }

                if (!isMounted) return;

                setIsLoading(false);
            } catch (error: any) {
                if (!isMounted) return;
                setIsLoading(false);
                setErrorMsg(error?.message || "Failed to load descendants.");
            }
        };

        loadFullGeneration();

        return () => {
            isMounted = false;
        };
    }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div className="container">
            <div className="d-flex flex-row justify-content-between align-items-center mb-3">
                <Link to={"/user-each/" + userId} className="btn btn-outline-secondary btn-sm">
                    <Icon.ArrowLeft className="me-1" /> Back to User Details
                </Link>
            </div>

            <Card>
                <Card.Header as="h4">
                    <div className="d-flex flex-row justify-content-between align-items-center">
                        <span>{user ? `${user.name} - Full Generation Tree` : "Full Generation Tree"}</span>
                        {user && <GenTag generationNo={user.generationNo} />}
                    </div>
                </Card.Header>

                <Card.Body>
                    {isLoading && !user ? (
                        <div className="py-5 position-relative">
                            <LoadingSpinner class="loading-spinner-md" />
                        </div>
                    ) : (
                        <GenerationTree
                            title={user ? `${user.name} Full Generation` : ""}
                            users={user ? [user] : null}
                            displayAllDescendants={true}
                            emptyMessage="No descendants found."
                        />
                    )}
                </Card.Body>
            </Card>
        </div>
    );
};

export default UserFullGenerationTreePage;
